import React, { useState, useEffect, memo } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { get, compact } from 'lodash';

// Constants
import { UPSELL_PLAN_BILLING_CYCLES, MONTHLY_PLAN_ID } from 'src/constants/plan';
// Utils
import { getAnnualAndMonthlySplitPlanPricing } from 'src/components/Plans/Pricing/utils';
// Components && Styles
import { CheckboxGroupV2, CheckboxV2 } from 'src/components/HTKit/Forms/CheckboxV2';
// tracking
import { updateTrackingByUpsell } from 'src/actions/tracking';
import { UPSELL_TRACKING } from 'src/constants/tracking';
// styles
import styles from './styles.scss';

const ProductSubscriptionupsell = ({
  className = 'marginTop-small1',
  selectedPlanId,
  plansInfo,
  amount,
  onToggle,
  onLearnMore,
  service,
}) => {
  const [checkedValues, setCheckedValues] = useState([]);
  const { MONTHLY } = UPSELL_PLAN_BILLING_CYCLES;
  const monthlyPlanId = get(plansInfo, [MONTHLY, 'id'], MONTHLY_PLAN_ID);
  const monthlyPlanAmount = get(plansInfo, [MONTHLY, 'amount']);
  const planName = get(plansInfo, [MONTHLY, 'name']);
  const dispatchReduxAction = useDispatch();

  useEffect(() => {
    setCheckedValues(compact([selectedPlanId]));
  }, [selectedPlanId]);

  /**
   * Component mounts. Fire segment viewed event
   */
  useEffect(() => {
    dispatchReduxAction(
      updateTrackingByUpsell({
        ...upsellTrackingProperties(),
        event: UPSELL_TRACKING.viewed,
      }),
    );
  }, []);

  const upsellTrackingProperties = () => {
    return {
      plan: { ...get(plansInfo, [MONTHLY], {}) },
      partner: service.partner,
    };
  };

  const onChange = (newSelection) => {
    const planAlreadySelected = checkedValues.length;
    if (!planAlreadySelected) {
      setCheckedValues(newSelection);
      onToggle(true, newSelection[0]);
    }
    if (planAlreadySelected) {
      if (!newSelection.length) {
        // checked box is clicked again (newSelection === [])
        setCheckedValues(newSelection);
        onToggle(false);
      }
      if (newSelection.length) {
        // multiple boxes are checked (newSelection === [1, 4])
        newSelection.shift();
        setCheckedValues(newSelection);
        onToggle(true, newSelection[0]);
      }
    }
  };

  const containerStyles = cn(className, styles.container, 'padding-small1', {
    [styles.active]: checkedValues.length,
  });

  const planValue = monthlyPlanId;
  const planAmount = monthlyPlanAmount;
  const { oldPrice, currentPrice, planPricingSplit } = getAnnualAndMonthlySplitPlanPricing(true);

  const Checkbox = () => (
    <CheckboxGroupV2 value={checkedValues} onChange={onChange}>
      <CheckboxV2 wrapperClass={styles.label} value={planValue} />
    </CheckboxGroupV2>
  );

  const UpsellPrice = () => {
    return planPricingSplit ? (
      <>
        <span className={cn('line-through paddingRight-tiny')}>{oldPrice}</span>
        <span>{currentPrice}</span>
      </>
    ) : (
      `$${planAmount}`
    );
  };
  const ctaText = amount ? `Save ${amount}` : 'get unlimited support';
  const ctaTextStyles = cn({ blue700: amount });
  return (
    <div className={containerStyles}>
      <div className="flex justifyContentSpaceBetween alignItemsCenter">
        <Checkbox />
      </div>
      <div>
        <h6 className="n800 marginBottom-tiny1">
          Become a {planName} Member and <span className={ctaTextStyles}>{ctaText}</span>
        </h6>
        <p className="caption n700">
          with <UpsellPrice /> /mo annual membership.{' '}
          <a href="#" className="caption blue700 cursorPointer" onClick={onLearnMore}>
            Learn More
          </a>
        </p>
      </div>
    </div>
  );
};

ProductSubscriptionupsell.propTypes = {
  className: PropTypes.string,
  active: PropTypes.bool,
  selectedPlanId: PropTypes.number,
  plansInfo: PropTypes.shape({
    yearly: PropTypes.shape({
      id: PropTypes.number,
      amount: PropTypes.number,
      planName: PropTypes.string,
    }),
    monthly: PropTypes.shape({
      id: PropTypes.number,
      amount: PropTypes.number,
    }),
  }),
  amount: PropTypes.string,
  onToggle: PropTypes.func,
  onLearnMore: PropTypes.func,
  service: PropTypes.object,
};

export default memo(ProductSubscriptionupsell);
